import React, { useMemo } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { getInitials } from '../../../utils/getInitials.js';
import IconRocket from '../../images/IconRocket.jsx';
import { Link } from 'react-router-dom';

const truncateText = (text, limit = 30) => {
    if (!text) return '';
    return text.length > limit ? text.slice(0, limit) + '...' : text;
};

const MainMatchCard = ({ model, data }) => {
    if (!data) return <div className='p-4 text-gray-500'>No data available.</div>;

    switch (model) {
        case 'Organization':
            return (
                <div className='border border-theme1-text3 rounded-full p-4 flex items-center gap-4 bg-white'>
                    <div className='w-12 h-12 bg-theme1-pale rounded-full flex items-center justify-center text-theme1-primary text-xl flex-shrink-0'>
                        {getInitials(data.name)}
                    </div>
                    <h2 className='text-xl font-bold text-theme1-text1 leading-none'>{truncateText(data.name)}</h2>
                </div>
            );
        default:
            return null;
    }
};

const getLeadData = (prefix, lead) => {
    if (prefix === 'from') {
        if (lead.from_model === 'Project') return lead.from_project;
        if (lead.from_model === 'Contact') return lead.from_contact;
        if (lead.from_model === 'Organization') return lead.from_organization;
    }
    if (prefix === 'to') {
        if (lead.to_model === 'Project') return lead.to_project;
        if (lead.to_model === 'Contact') return lead.to_contact;
        if (lead.to_model === 'Organization') return lead.to_organization;
    }
    return null;
};

const getExtraInfo = (prefix, detail) => {
    if (prefix === 'from') {
        if (detail.from_model === 'Contact' && detail.from_contact) {
            return `${detail.from_contact.first_name} ${detail.from_contact.last_name}`;
        }
        if (detail.from_model === 'Project' && detail.from_project) {
            return detail.from_project.name;
        }
    }
    if (prefix === 'to') {
        if (detail.to_model === 'Contact' && detail.to_contact) {
            return `${detail.to_contact.first_name} ${detail.to_contact.last_name}`;
        }
        if (detail.to_model === 'Project' && detail.to_project) {
            return detail.to_project.name;
        }
    }
    return null;
};

const getModelLink = (model, prefix, obj) => {
    const id = obj[`${prefix}_id`];
    switch (model) {
        case 'Organization':
            return `/organizations/${id}`;
        case 'Project':
            return `/projects/${id}`;
        case 'Contact':
            return `/contacts/view/${id}`;
        default:
            return '#';
    }
};

const MatchSkillsList = ({ lead }) => {
    const detailsBySkill = useMemo(() => {
        return lead.details.reduce((acc, detail) => {
            const { skill } = detail;
            if (!acc[skill]) {
                acc[skill] = [];
            }
            acc[skill].push(detail);
            return acc;
        }, {});
    }, [lead.details]);

    return (
        <Accordion
            className={`custom-accordion custom-accordion-lead-skills ${lead.from_model !== 'Organization' && lead.to_model !== 'Organization' ? 'custom-accordion-lead-skills-inactive' : ''}`}
        >
            {Object.keys(detailsBySkill).map((skill) => {
                const details = detailsBySkill[skill];

                if (lead.from_model !== 'Organization' && lead.to_model !== 'Organization') {
                    return (
                        <AccordionTab
                            className='custom-accordion-tab-disabled'
                            header={skill}
                            key={skill}
                        ></AccordionTab>
                    );
                }

                const hasFromDetails = details.some((detail) => !!getExtraInfo('from', detail));
                const hasToDetails = details.some((detail) => !!getExtraInfo('to', detail));

                return (
                    <AccordionTab header={skill} key={skill} className='custom-accordion-lead-skills-padding'>
                        <div className='flex flex-col sm:flex-row gap-4 mb-4'>
                            {lead.from_model === 'Organization' && (
                                <div className='relative flex-1'>
                                    <Link to={getModelLink(lead.from_model, 'from', lead)}>
                                        <MainMatchCard model={lead.from_model} data={getLeadData('from', lead)} />
                                    </Link>
                                    {hasFromDetails && (
                                        <div
                                            className='absolute left-6 top-[5.5rem] w-px bg-theme1-text3 ml-4'
                                            style={{ height: 'calc(100% - 7rem)' }}
                                        />
                                    )}
                                    {details.map((detail) => {
                                        const info = getExtraInfo('from', detail);
                                        const avatar = detail.from_contact?.avatar;
                                        return info ? (
                                            <Link to={getModelLink(detail.from_model, 'from', detail)} key={detail.id}>
                                                <div className='relative pl-10 mt-4 ml-10'>
                                                    {/* Horizontal line */}
                                                    <div className='absolute left-0 top-1/2 w-8 h-px bg-theme1-text3' />
                                                    {/* Arrowhead */}
                                                    <div
                                                        className='absolute left-8 top-1/2 -translate-y-1/2
                   w-0 h-0 border-y-4 border-y-transparent
                   border-l-4 border-l-theme1-text3'
                                                    />
                                                    {/* Text */}
                                                    <div className='border border-theme1-text3 rounded-full p-2 flex items-center gap-2 bg-white'>
                                                        {avatar ? (
                                                            <img
                                                                src={avatar}
                                                                alt={info}
                                                                className='w-8 h-8 rounded-full flex-shrink-0'
                                                            />
                                                        ) : (
                                                            <>
                                                                {detail.from_project ? (
                                                                    <div className='bg-theme1-gold rounded-full flex-shrink-0 flex items-center justify-center w-8 h-8'>
                                                                        <IconRocket className='stroke-theme1-white w-5 h-5' />
                                                                    </div>
                                                                ) : (
                                                                    <div className='w-8 h-8 bg-theme1-pale rounded-full flex items-center justify-center text-theme1-primary text-sm flex-shrink-0'>
                                                                        {getInitials(info)}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        <h2 className='text-xs font-bold text-theme1-text1'>{info}</h2>
                                                    </div>
                                                </div>
                                            </Link>
                                        ) : null;
                                    })}
                                </div>
                            )}
                            {lead.to_model === 'Organization' && (
                                <div className='relative flex-1'>
                                    <Link to={getModelLink(lead.to_model, 'to', lead)}>
                                        <MainMatchCard model={lead.to_model} data={getLeadData('to', lead)} />
                                    </Link>
                                    {hasToDetails && (
                                        <div
                                            className='absolute left-6 top-[5.5rem] w-px bg-theme1-text3 ml-4'
                                            style={{ height: 'calc(100% - 7rem)' }}
                                        />
                                    )}

                                    {details.map((detail) => {
                                        const info = getExtraInfo('to', detail);
                                        const avatar = detail.to_contact?.avatar;
                                        return info ? (
                                            <Link to={getModelLink(detail.to_model, 'to', detail)} key={detail.id}>
                                                <div className='relative pl-10 mt-4 ml-10'>
                                                    {/* Horizontal line */}
                                                    <div className='absolute left-0 top-1/2 w-8 h-px bg-theme1-text3' />
                                                    {/* Arrowhead */}
                                                    <div
                                                        className='absolute left-8 top-1/2 -translate-y-1/2
                   w-0 h-0 border-y-4 border-y-transparent
                   border-l-4 border-l-theme1-text3'
                                                    />
                                                    {/* Text */}
                                                    <div className='border border-theme1-text3 rounded-full p-2 flex items-center gap-2 bg-white'>
                                                        {avatar ? (
                                                            <img
                                                                src={avatar}
                                                                alt={info}
                                                                className='w-8 h-8 rounded-full flex-shrink-0'
                                                            />
                                                        ) : (
                                                            <>
                                                                {detail.to_project ? (
                                                                    <div className='bg-theme1-gold rounded-full flex-shrink-0 flex items-center justify-center w-8 h-8'>
                                                                        <IconRocket className='stroke-theme1-white w-5 h-5' />
                                                                    </div>
                                                                ) : (
                                                                    <div className='w-8 h-8 bg-theme1-pale rounded-full flex items-center justify-center text-theme1-primary text-sm flex-shrink-0'>
                                                                        {getInitials(info)}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        <h2 className='text-xs font-bold text-theme1-text1'>{info}</h2>
                                                    </div>
                                                </div>
                                            </Link>
                                        ) : null;
                                    })}
                                </div>
                            )}
                        </div>
                    </AccordionTab>
                );
            })}
        </Accordion>
    );
};

export default MatchSkillsList;
